import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { useAuthStore } from '../lib/auth'

export default function Register() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('session_id')
  const { user, profile, register, registerOAuth } = useAuthStore()

  // isOAuth: user is already signed in via Google OAuth but has no profile yet
  const isOAuth = Boolean(user && !profile && sessionId)

  // Guard: redirect cases
  useEffect(() => {
    if (user && profile) {
      // Already fully registered
      navigate('/dashboard')
      return
    }
    if (!sessionId) {
      navigate('/plans')
      return
    }
  }, [user, profile, sessionId, navigate])

  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    city: '',
    state: '',
    termsAccepted: false
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Show nothing while checking redirects
  if ((user && profile) || !sessionId) return null

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (!formData.termsAccepted) {
      setError('You must accept the Terms of Service to register.')
      return
    }

    setLoading(true)

    try {
      if (isOAuth) {
        // User is already authenticated via Google — just create the profile
        await registerOAuth({
          username: formData.username,
          termsAccepted: formData.termsAccepted
        })
        localStorage.removeItem('oauth_new_user')
        navigate('/dashboard')
      } else {
        await register({
          email: formData.email,
          password: formData.password,
          username: formData.username,
          firstName: formData.firstName,
          lastName: formData.lastName,
          city: formData.city,
          state: formData.state,
          termsAccepted: formData.termsAccepted
        })
        navigate('/verify-email', { state: { email: formData.email } })
      }
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0e17] text-white">
      <Header />

      <div className="flex items-center justify-center px-4 py-12">
        <div className="card max-w-md w-full">
          <h2 className="text-3xl font-bold mb-2 text-center" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            {isOAuth ? 'One Last Step' : 'Create Account'}
          </h2>
          <p className="text-gray-400 text-center mb-6">
            {isOAuth
              ? `Signed in as ${user?.email}. Choose your HireMail username to complete setup.`
              : 'Join ScamView to protect yourself from recruitment scams'}
          </p>

          {sessionId && !isOAuth && (
            <div className="bg-green-500/20 text-green-400 p-3 rounded-lg mb-4 text-center border border-green-500/30">
              Payment confirmed! Complete your registration below.
            </div>
          )}

          {error && (
            <div className="bg-red-500/20 text-red-400 p-3 rounded-lg mb-4 text-center border border-red-500/30">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* OAuth banner */}
            {isOAuth && (
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg px-4 py-3 text-sm text-cyan-300 flex items-center gap-2">
                <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Payment confirmed! Google sign-in verified.
              </div>
            )}

            {/* Name + email + city/state + password — email/password users only */}
            {!isOAuth && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">First Name</label>
                    <input type="text" name="firstName" className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition" value={formData.firstName} onChange={handleChange} required placeholder="First name" />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">Last Name</label>
                    <input type="text" name="lastName" className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition" value={formData.lastName} onChange={handleChange} required placeholder="Last name" />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 mb-2 text-sm">Email</label>
                  <input type="email" name="email" className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition" value={formData.email} onChange={handleChange} required placeholder="you@example.com" />
                </div>
              </>
            )}

            {/* Username — always shown */}
            <div>
              <label className="block text-gray-400 mb-2 text-sm">
                Username <span className="text-gray-500">(your @hiremail.net alias)</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="username"
                  className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 pr-32 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  placeholder="john.doe"
                  pattern="[a-zA-Z0-9._]+"
                  title="Only letters, numbers, dots, and underscores"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">@hiremail.net</span>
              </div>
            </div>

            {/* City/state + password — email/password users only */}
            {!isOAuth && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">City</label>
                    <input type="text" name="city" className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition" value={formData.city} onChange={handleChange} placeholder="City" />
                  </div>
                  <div>
                    <label className="block text-gray-400 mb-2 text-sm">State</label>
                    <input type="text" name="state" className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition" value={formData.state} onChange={handleChange} placeholder="State" />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 mb-2 text-sm">Password</label>
                  <input type="password" name="password" className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition" value={formData.password} onChange={handleChange} required placeholder="At least 8 characters" minLength={8} />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2 text-sm">Confirm Password</label>
                  <input type="password" name="confirmPassword" className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition" value={formData.confirmPassword} onChange={handleChange} required placeholder="Re-enter password" />
                </div>
              </>
            )}

            {/* Terms — always shown */}
            <div className="flex items-start gap-3">
              <input type="checkbox" name="termsAccepted" id="termsAccepted" checked={formData.termsAccepted} onChange={handleChange} className="mt-1 w-4 h-4 accent-cyan-500" required />
              <label htmlFor="termsAccepted" className="text-gray-400 text-sm">
                I agree to the{' '}
                <Link to="/terms" className="text-cyan-400 hover:text-cyan-300 transition underline" target="_blank">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300 transition underline" target="_blank">Privacy Policy</Link>
              </label>
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full py-3 mt-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? (isOAuth ? 'Setting up…' : 'Creating Account...') : (isOAuth ? 'Complete Setup' : 'Create Account')}
            </button>
          </form>

          <p className="mt-6 text-center text-gray-400 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-cyan-400 hover:text-cyan-300 transition">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <Footer />
    </div>
  )
}
